import type { ErrorEvent, EventHint } from '@sentry/react';

import { isAbortError, isAbortLikeMessage } from '../shared/common/utils/cancellable-list-fetch';
import { isNetworkFetchError } from '../shared/common/utils/network-fetch-error';

const AUTH_SESSION_MESSAGES = new Set([
  'No refresh token specified.',
  'Couldn\'t refresh token.',
]);

function isApiErrorBodyShape(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false;

  const record = value as Record<string, unknown>;
  return (
    typeof record.detail === 'string'
    && typeof record.status_code === 'number'
    && typeof record.type === 'string'
  );
}

function isClientApiErrorBody(value: unknown): boolean {
  if (!isApiErrorBodyShape(value)) return false;

  const statusCode = (value as Record<string, unknown>).status_code as number;
  return statusCode < 500;
}

function getOriginalException(hint: EventHint): unknown {
  const original = hint.originalException;

  if (original instanceof CustomEvent && original.type === 'unhandledrejection') {
    return (original as CustomEvent).detail;
  }

  return original;
}

function isBenignUnhandledRejection(exception: unknown): boolean {
  if (exception === undefined || exception === null) return true;
  if (exception instanceof Error) return false;
  if (typeof exception === 'object' && !Array.isArray(exception) && Object.keys(exception).length === 0) {
    return true;
  }

  return false;
}

function isInjectedModuleLoaderStack(event: ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  if (!frames.length) return false;

  const functions = frames.map((frame) => frame.function ?? '');
  const hasInjectedFire = functions.some(
    (fn) => fn.includes('window.__fire') || fn === '__fire',
  );
  const hasInjectedAddModule = functions.some((fn) => fn.includes('__addModule'));

  // DASHBOARD-58H: extension/eval loader chain — require fire + addModule, not bare __build (e.g. Cart.__build).
  return hasInjectedFire && hasInjectedAddModule;
}

function isBenignBrowserNoise(message: string): boolean {
  return (
    message.includes('ResizeObserver loop')
    || message.includes('Unable to preload CSS')
    || message.includes('Loading CSS chunk')
    || message.includes('Access is denied for this document')
    || message.includes('delete range from database without an in-progress transaction')
    || message.includes('Registration does not have an active worker')
    || message === 'Load failed'
    || message.includes('Non-Error exception captured with keys: detail, error_code')
    || message.includes('Object captured as exception with keys: detail, error_code')
    || message.includes('getCharacterList')
    || message === '[object Promise]'
  );
}

function isThenable(value: unknown): boolean {
  return !!value && typeof value === 'object' && typeof (value as { then?: unknown }).then === 'function';
}

export function shouldDropCapturedException(exception: unknown): boolean {
  if (isThenable(exception)) return true;
  if (isBenignUnhandledRejection(exception)) return true;
  if (isAbortError(exception)) return true;
  if (isNetworkFetchError(exception)) return true;
  if (isClientApiErrorBody(exception)) return true;

  if (exception instanceof Error) {
    if (AUTH_SESSION_MESSAGES.has(exception.message)) return true;
    if (isBenignBrowserNoise(exception.message)) return true;
  }

  return false;
}

export function shouldDropSentryEvent(event: ErrorEvent, hint: EventHint): boolean {
  if (shouldDropCapturedException(getOriginalException(hint))) return true;
  if (isInjectedModuleLoaderStack(event)) return true;

  const message = event.message || event.exception?.values?.[0]?.value || '';
  if (typeof message === 'string' && isAbortLikeMessage(message)) return true;
  if (typeof message === 'string' && message.includes('No refresh token specified')) return true;
  if (typeof message === 'string' && isBenignBrowserNoise(message)) return true;

  return false;
}
